<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { useLs } from '@/setup/vueLs';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import updateUrl from '@/service/updateUrl';
import _ from '@/setup/lodash';
import type {
  GetMediaBuyingReportsUnreportedFundsData,
  GetMediaBuyingReportsUnreportedFundsResponse,
} from '@/types/openapi-ts';
//#endregion

//#region types
type MediaBuyingReportsUnreportedFundsRow = GetMediaBuyingReportsUnreportedFundsResponse['payload'][number];
type MediaBuyingReportsUnreportedFundsPermissions = NonNullable<GetMediaBuyingReportsUnreportedFundsResponse['misc']>['permissions'];
//#endregion

//#region const
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';

const LS_PATH = 'MediaBuying/UnreportedFunds';

const sizes = {
  1080: 20,
  1440: 30,
};

const fields = [
  {
    name: 'affiliate_email',
    align: 'left',
    sortable: 'custom',
    link: getAffiliateLink,
  },
  {
    name: 'campaigns_without_posts_count',
    align: 'right',
    width: '250',
    sortable: 'custom',
  },
  {
    name: 'unreported_sum',
    align: 'right',
    width: '150',
    sortable: 'custom',
    format: 'formatMoney',
  },
  {
    name: 'reported_sum',
    align: 'right',
    width: '150',
    sortable: 'custom',
    format: 'formatMoney',
  },
] satisfies UiTableNewField<MediaBuyingReportsUnreportedFundsRow>[];
//#endregion

//#region composables
const store = useTypedStore();
const route = useRoute();
const api = useApi();
const ls = useLs();
const { checkCurrencyCode } = useFormatCurrency();

const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'affiliate_email',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region state
const currencyCode = ref('');

const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<MediaBuyingReportsUnreportedFundsRow[]>([]);
const permissions = ref<MediaBuyingReportsUnreportedFundsPermissions>({});
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
//#endregion

//#region methods
const _query = () => {
  return {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    currency_code: currencyCode.value,
  } as GetMediaBuyingReportsUnreportedFundsData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset']),
    currency_code: currencyCode.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getUnreportedFunds(options);

    count.value = misc!.count;
    permissions.value = misc!.permissions;
    currencyCode.value = misc!.currency_code;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);

    delete _urlData.page;
    delete _urlData.limit;

    _.merge(optionsSort.value, _urlData);

    setSort(_urlData);
    setSearch(_urlData);
    currencyCode.value = checkCurrencyCode(_.get(_urlData, ['currency_code']) as string);
  } else {
    currencyCode.value = checkCurrencyCode(ls.get(`${LS_PATH}/currency_code`));
  }

  nextTick(() => {
    fetchData();
  });
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

function changeCurrency(value: string) {
  currencyCode.value = value;
  ls.set(`${LS_PATH}/currency_code`, value);
  fetchData();
}

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

function getAffiliateLink(row: MediaBuyingReportsUnreportedFundsRow) {
  const isUser = !adminAcl.value.is_superuser && permissions.value[row.affiliate_id]!.view;
  const isSuperUser = adminAcl.value.is_superuser;
  return isUser || isSuperUser ? `/affiliates/${row.affiliate_id}/info` : '';
}

function calcPermissions(row: MediaBuyingReportsUnreportedFundsRow, item: 'view') {
  return permissions.value[row.affiliate_id]![item];
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="media-buying-unreported-funds">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('mediaBuying.page_title.media_buying_unreported_funds.name') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiCurrencyPicker
          :model-value="currencyCode"
          :currencies="customerCurrencies"
          @update:model-value="changeCurrency"
        />
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :currency="currencyCode"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="mediaBuying.list_table_title"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
              class="table-action-header"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              v-if="calcPermissions(scope.row,'view')"
              icon="user"
              :tooltip="$t('mediaBuying.actions.profile')"
              :router-to="getAffiliateLink(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
